import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthLayout, CodeInput, Button, HiyaLogo } from "@hiya/ui";
import { confirmSignInWithCode, signOut } from "../../auth/auth-service";

export function OtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email ?? "";
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // HH-250: clear the Cognito session BEFORE routing to /login. A plain
  // <Link to="/login"> left the access token alive, and LoginPage's auto-
  // redirect would silently send any user with a live token to /dashboard
  // without credentials or OTP re-entry. If signOut fails (network, etc.),
  // still navigate so the user is not stuck on the OTP screen — LoginPage
  // will handle the retry. Also wired to AuthLayout's back chevron so the
  // same bypass is not reachable via the top-left arrow.
  async function handleSignInAgain() {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      // swallow — we still want the user back on /login
    } finally {
      navigate("/login", { replace: true });
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await confirmSignInWithCode(code, email);
      if (result.isSignedIn) {
        navigate("/dashboard");
      } else {
        setError("Verification incomplete. Please try again.");
      }
    } catch {
      setError("The code doesn't match -- please try again.");
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Check your Email"
      subtitle="We've sent a 6-digit verification code to your email. Enter the code below to continue."
      icon={<HiyaLogo />}
      onBack={handleSignInAgain}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-8 w-full">
        <CodeInput value={code} onChange={setCode} hasError={!!error} />
        {error && (
          <p className="text-xs font-medium text-input-hint-error text-center">{error}</p>
        )}
        <div className="flex flex-col gap-4 items-center w-full">
          <Button type="submit" disabled={code.length < 6 || loading}>
            Log in
          </Button>
          <p className="text-sm text-text-primary text-center">
            {"Didn't receive a code? "}
            <button
              type="button"
              onClick={handleSignInAgain}
              disabled={signingOut}
              className="font-semibold text-brand-brown underline disabled:opacity-60"
            >
              Sign in again
            </button>
            {" to get a new one."}
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}
